import { useState, useEffect, useCallback } from 'react'
import { notasVentaApi } from '../api/notasVenta'
import Modal from './Modal'
import Boton from './Boton'
import MoneyInput from './MoneyInput'
import TraerCobroPasarelaModal from './TraerCobroPasarelaModal'
import { copiarTexto } from '../utils/copiar'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'
import { colors, font, text, sx, radius } from '../design/tokens'

const PASARELA = { mobbex: 'Mobbex', mercadopago: 'Mercado Pago' }
const ESTADO = {
  pendiente: { label: 'Esperando el pago', bg: colors.warnBg, color: colors.warn },
  pagado: { label: 'Pagado', bg: colors.okBg, color: colors.ok },
  anulado: { label: 'Anulado', bg: colors.bgSoft, color: colors.textSec },
}
// Pendiente en LGR pero Mobbex ya no lo cobra: hay que generar otro.
const VENCIDO = { label: 'Vencido sin pagar', bg: colors.bgSoft, color: colors.textSec }

/**
 * Card "💳 Links de pago" del detalle de NV (Martin, 2 oct 2026, caso Alexis
 * Stefan): el link de Mobbex / Mercado Pago se genera DESDE la NV, por el monto
 * que elige el vendedor. Cuando el cliente paga —aunque sea días después y con
 * la NV archivada— el cobro entra solo a esta NV y se avisa al vendedor. Un
 * link armado a mano en el panel de la pasarela, en cambio, LGR nunca lo ve.
 */
export default function LinksPagoNVCard({ nv, onChange }) {
  const [data, setData] = useState({ saldo: 0, links: [] })
  const [abierto, setAbierto] = useState(false)
  const [traer, setTraer] = useState(false)
  const [copiado, setCopiado] = useState(null)
  const [error, setError] = useState('')

  const cargar = useCallback(async () => {
    try {
      const { data: res } = await notasVentaApi.linksPago(nv.id)
      setData(res)
    } catch { /* error transitorio: la card no molesta */ }
  }, [nv.id])

  useEffect(() => { cargar() }, [cargar, nv.pago_estado, nv.archivado])

  if (nv.estado === 'anulado') return null

  const copiar = async (link) => {
    if (await copiarTexto(link.url)) {
      setCopiado(link.id)
      setTimeout(() => setCopiado(null), 2000)
    }
  }

  const anular = async (link) => {
    if (!window.confirm(`¿Anular el link ${link.numero} por $ ${fmtMonto(link.monto)}? Si el cliente igual lo paga, no se registra y te avisa.`)) return
    setError('')
    try {
      const { data: res } = await notasVentaApi.anularLinkPago(nv.id, link.id)
      setData(d => ({ ...d, links: res.links }))
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo anular el link.')
    }
  }

  return (
    <div style={{ ...sx.card, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 10, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: text.title }}>💳 Links de pago</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Boton variante="ghost" sm onClick={() => setTraer(true)} title="El link se armó en el panel de Mobbex o Mercado Pago y el cliente ya pagó: traé ese cobro con el número de operación del comprobante">Traer cobro hecho por fuera</Boton>
          <Boton variante="secondary" sm onClick={() => setAbierto(true)}>Generar link de pago</Boton>
        </div>
      </div>

      {data.links.length === 0 && (
        <p style={{ fontSize: text.sec, color: colors.textMeta, margin: 0 }}>
          Generá el link acá y no en el panel de Mobbex o Mercado Pago: cuando el cliente pague —aunque sea días
          después y con la NV archivada— el cobro entra solo a esta NV y te llega el aviso.
        </p>
      )}

      {error && <div style={{ fontSize: text.sec, color: colors.err, marginBottom: 6 }}>{error}</div>}

      {data.links.map(l => {
        const es = l.vencido ? VENCIDO : (ESTADO[l.estado] || ESTADO.pendiente)
        return (
          <div key={l.id} style={st.fila}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: font.mono, fontSize: text.sec, color: colors.textSec }}>{l.numero}</span>
                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>$ {fmtMonto(l.monto)}</strong>
                <span style={{ fontSize: text.sec, color: colors.textSec }}>{PASARELA[l.gateway] || l.gateway}</span>
                <span style={{ ...st.chip, background: es.bg, color: es.color }}>
                  {es.label}{l.estado === 'pagado' && l.pagado_at ? ` · ${fmtFecha(l.pagado_at)} ${fmtHoraDe(l.pagado_at)}` : ''}
                </span>
              </div>
              <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 2 }}>
                {l.concepto ? `${l.concepto} · ` : ''}creado {fmtFecha(l.creado_at)} {fmtHoraDe(l.creado_at)}{l.creado_por ? ` por ${l.creado_por}` : ''}
                {l.estado === 'pendiente' && l.vence_at ? ` · ${l.vencido ? 'venció' : 'vence'} ${fmtFecha(l.vence_at)} ${fmtHoraDe(l.vence_at)}` : ''}
              </div>
              {l.gateway === 'mobbex' && l.vence_at && (
                <div style={{ fontSize: text.meta, color: l.cuotas ? colors.ok : colors.textMeta, marginTop: 2 }}>
                  {l.cuotas ? `💳 ${l.cuotas}` : 'Sin cuotas sin interés'}
                </div>
              )}
            </div>
            {l.estado === 'pendiente' && !l.vencido && (
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Boton variante="send" sm onClick={() => copiar(l)}>{copiado === l.id ? '✓ Copiado' : 'Copiar link'}</Boton>
                <Boton variante="ghost" sm onClick={() => anular(l)}>Anular</Boton>
              </div>
            )}
          </div>
        )
      })}

      {traer && (
        <TraerCobroPasarelaModal nv={nv} onClose={() => setTraer(false)} onRegistrado={() => { setTraer(false); onChange?.() }} />
      )}

      {abierto && (
        <ModalGenerarLink
          nv={nv}
          saldo={data.saldo}
          cuotas={data.cuotas}
          onClose={() => setAbierto(false)}
          onCreado={(res) => {
            setData(d => ({ ...d, links: res.links }))
            onChange?.()
          }}
        />
      )}
    </div>
  )
}

function ModalGenerarLink({ nv, saldo, cuotas, onClose, onCreado }) {
  const [gateway, setGateway] = useState('mobbex')
  const [monto, setMonto] = useState(saldo > 0 ? String(saldo) : '')
  const [concepto, setConcepto] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  const [creado, setCreado] = useState(null)
  const [copiado, setCopiado] = useState(false)

  const generar = async () => {
    const n = parseFloat(monto)
    if (!n || n <= 0) { setError('Poné el monto a cobrar.'); return }
    setEnviando(true); setError('')
    try {
      const { data } = await notasVentaApi.crearLinkPago(nv.id, { gateway, monto: n, concepto: concepto.trim() || null })
      setCreado(data.link)
      onCreado(data)
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo generar el link.')
    } finally { setEnviando(false) }
  }

  const copiar = async () => {
    if (await copiarTexto(creado.url)) setCopiado(true)
  }

  return (
    <Modal title="Generar link de pago" onClose={onClose} maxWidth="460px">
      {creado ? (
        <div>
          <p style={{ margin: '0 0 8px', fontSize: text.body }}>
            Link <span style={{ fontFamily: font.mono }}>{creado.numero}</span> por <strong>$ {fmtMonto(creado.monto)}</strong> ({PASARELA[creado.gateway]}). Copialo y mandáselo al cliente.
          </p>
          <div style={st.url}>{creado.url}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
            <Boton variante="primary" onClick={copiar}>{copiado ? '✓ Copiado' : 'Copiar link'}</Boton>
          </div>
        </div>
      ) : (
        <div>
          <div style={st.campo}>
            <span style={st.label}>Pasarela</span>
            <div style={{ display: 'flex', gap: 14 }}>
              {Object.entries(PASARELA).map(([k, v]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: text.body, cursor: 'pointer' }}>
                  <input type="radio" name="gateway" checked={gateway === k} onChange={() => setGateway(k)} /> {v}
                </label>
              ))}
            </div>
          </div>
          <AvisoCuotas gateway={gateway} cuotas={cuotas} />
          <div style={st.campo}>
            <span style={st.label}>Monto a cobrar</span>
            <MoneyInput value={monto} onChange={setMonto} style={sx.input} autoFocus />
            <span style={{ fontSize: text.meta, color: colors.textMeta }}>
              Falta cobrar de esta NV: $ {fmtMonto(saldo)}. Podés cambiarlo (por ejemplo, solo el envío).
            </span>
          </div>
          <div style={st.campo}>
            <span style={st.label}>Concepto (lo ve el cliente al pagar)</span>
            <input value={concepto} onChange={e => setConcepto(e.target.value)} maxLength={120} placeholder={`Ligier — ${nv.numero_lgr}`} style={sx.input} />
          </div>
          {error && <div style={{ fontSize: text.sec, color: colors.err, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
            <Boton variante="primary" disabled={enviando} onClick={generar}>{enviando ? 'Generando…' : 'Generar link'}</Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

// Qué cuotas sin interés va a ofrecer el link (sugerencia #116): las mismas
// que el carrito tendría en la tienda, sea por el monto que sea.
function AvisoCuotas({ gateway, cuotas }) {
  if (!cuotas) return null
  if (gateway !== 'mobbex') {
    return <div style={st.aviso}>Mercado Pago no tiene cuotas sin interés.{cuotas.resumen ? ' Para que el cliente pague en cuotas, generá el link por Mobbex.' : ''}</div>
  }
  if (cuotas.resumen) {
    return (
      <div style={{ ...st.aviso, background: colors.okBg, borderColor: colors.ok, color: colors.text }}>
        <strong style={{ color: colors.ok }}>💳 Este link ofrece {cuotas.resumen}.</strong>
        {cuotas.solo_hoy && <div style={{ marginTop: 4 }}>Son de la promo de hoy: el link vence hoy a las 23:59.</div>}
      </div>
    )
  }
  const porque = cuotas.motivo === 'membresia'
    ? 'la membresía del Wine Club se cobra como suscripción'
    : cuotas.motivo === 'mixto'
      ? 'el carrito mezcla productos con y sin cuotas propias'
      : 'estos productos no tienen cuotas propias'
  return (
    <div style={st.aviso}>
      Este link sale sin cuotas sin interés: {porque}.
      {cuotas.motivo !== 'membresia' && cuotas.proxima_promo && <> El {fmtFecha(cuotas.proxima_promo)} hay {cuotas.promo}: si el cliente las quiere, generá el link ese día.</>}
    </div>
  )
}

const st = {
  aviso: { fontSize: text.sec, color: colors.textSec, background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '8px 10px', marginBottom: 12 },
  fila: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${colors.borderSoft}` },
  chip: { display: 'inline-block', padding: '2px 8px', borderRadius: radius.sm, fontSize: text.meta, fontWeight: 600, whiteSpace: 'nowrap' },
  campo: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  label: { fontSize: text.sec, fontWeight: 600, color: colors.textSec },
  url: { fontFamily: font.mono, fontSize: text.sec, background: colors.bgSoft, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '8px 10px', overflowWrap: 'anywhere' },
}
